import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Printer, Mail, FileDown, Receipt, FileText } from "lucide-react";
import { API_URL, formatCurrency } from "../../lib/utils";
import { printReceipt } from "../../lib/invoicePrint";
import { toastApiError } from "../../lib/apiErrors";
import { EmailHistory } from "../common/EmailHistory";
import { openPdfDocument } from "../../lib/docViewer";

export const ReceiptActions = ({ receipt, settings, customerEmail, compact = false, onSent }) => {
  const [sending, setSending] = useState(false);
  const [emails, setEmails] = useState(receipt?.emails || []);
  if (!receipt) return null;

  const openPdf = async () => {
    try {
      const r = await axios.get(`${API_URL}/receipts/${receipt.document_id}/pdf`, { withCredentials: true, responseType: "blob" });
      openPdfDocument(r.data, { title: `Recibo ${receipt.document_number}`, filename: `${receipt.document_number.replace(/[ /]/g, "_")}.pdf` });
    } catch { toast.error("Não foi possível gerar o PDF"); }
  };

  const sendEmail = async () => {
    setSending(true);
    try {
      const r = await axios.post(`${API_URL}/receipts/${receipt.document_id}/email`, {}, { withCredentials: true });
      toast.success(r.data.message || "Recibo enviado");
      if (r.data.entry) setEmails((l) => [...l, r.data.entry]);
      onSent?.(r.data.entry);
    } catch (err) { toastApiError(err, "Erro ao enviar o recibo"); }
    finally { setSending(false); }
  };

  const size = compact ? "sm" : "default";
  return (
    <div className={`${compact ? "" : "rounded-lg border border-emerald-200 bg-emerald-50 p-3"}`} data-testid={`receipt-actions-${receipt.document_id}`}>
    <div className="flex flex-wrap items-center gap-2">
      {!compact && (
        <div className="flex-1 min-w-[12rem]">
          <p className="text-xs font-semibold uppercase text-emerald-700 flex items-center gap-1"><Receipt className="w-3.5 h-3.5" /> Recibo emitido</p>
          <p className="font-mono font-bold" data-testid="receipt-number">{receipt.document_number}</p>
          <p className="text-xs text-muted-foreground">{formatCurrency(receipt.total)} · {(receipt.settled_invoices || []).map((s) => s.invoice_number).join(", ")}</p>
        </div>
      )}
      <Button type="button" size={size} variant="outline" onClick={() => printReceipt(receipt, settings)} data-testid="receipt-print-btn"><Printer className="w-4 h-4 mr-1" /> Imprimir</Button>
      <Button type="button" size={size} variant="outline" onClick={openPdf} data-testid="receipt-pdf-btn"><FileDown className="w-4 h-4 mr-1" /> PDF</Button>
      <Button type="button" size={size} variant="outline" onClick={sendEmail} disabled={sending || !customerEmail} title={customerEmail ? `Enviar para ${customerEmail}` : "Cliente sem email na ficha"} data-testid="receipt-email-btn"><Mail className="w-4 h-4 mr-1" /> {sending ? "A enviar…" : customerEmail ? "Enviar por email" : "Sem email"}</Button>
    </div>
    <div className={compact ? "mt-1" : "mt-2"}><EmailHistory emails={emails} compact testId={`receipt-email-history-${receipt.document_id}`} /></div>
    </div>
  );
};

export const StatementActions = ({ customerId, customerEmail }) => {
  const [sending, setSending] = useState(false);

  const openPdf = async () => {
    try {
      const r = await axios.get(`${API_URL}/customers/${customerId}/statement.pdf`, { withCredentials: true, responseType: "blob" });
      openPdfDocument(r.data, { title: "Extrato de conta corrente", filename: "extrato_conta_corrente.pdf" });
    } catch { toast.error("Não foi possível gerar o extrato"); }
  };

  const sendEmail = async () => {
    setSending(true);
    try {
      const r = await axios.post(`${API_URL}/customers/${customerId}/statement/email`, {}, { withCredentials: true });
      toast.success(r.data.message || "Extrato enviado");
    } catch (err) { toastApiError(err, "Erro ao enviar o extrato"); }
    finally { setSending(false); }
  };

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="statement-actions">
      <Button type="button" size="sm" variant="outline" onClick={openPdf} data-testid="statement-pdf-btn"><FileText className="w-4 h-4 mr-1" /> Extrato PDF</Button>
      <Button type="button" size="sm" variant="outline" onClick={sendEmail} disabled={sending || !customerEmail} title={customerEmail ? `Enviar para ${customerEmail}` : "Cliente sem email na ficha"} data-testid="statement-email-btn"><Mail className="w-4 h-4 mr-1" /> {sending ? "A enviar…" : customerEmail ? "Enviar extrato por email" : "Sem email"}</Button>
    </div>
  );
};
